import { AnyAction } from 'redux';
import { AddVertragToAngebote, GlobalActions, VorbelegungPayload } from '@ps20-frontend/shop-integration-redux';
import { App, getAnzahlVorschaeden, VhvVorbelegungdaten } from '@ps20-frontend/frontend-models';
import { TaasTarifrisikoschluesselVHV } from '@ps20/bff-models';
import { IAppState } from '../appState';
import { VhvActions } from '../vhvActions';

export interface Vorschaeden {
  vorhanden: boolean | null;
  fels: number;
  fahrrad: number;
  elementar: number;
  felsTouched?: boolean;
  fahrradTouched?: boolean;
  elementarTouched?: boolean;
  elementarVisibleInErsatzgeschaeft?: boolean;
}

type VorschadenArt = 'fels' | 'fahrrad' | 'elementar';

const resolveVorschadenArt = (
  sectionState: Vorschaeden,
  art: VorschadenArt,
  fallbackCount: number,
): { count: number; touched: boolean } => {
  const touchedKey = `${art}Touched` as const;
  if (sectionState[touchedKey]) {
    return { count: sectionState[art], touched: true };
  }
  return { count: fallbackCount, touched: false };
};

export const vorschaedenReducer = function (
  sectionState: Vorschaeden = null,
  action: AnyAction,
  appState: IAppState,
): Vorschaeden {
  switch (action.type) {
    case VhvActions.VORSCHAEDEN_CHANGED: {
      return {
        ...sectionState,
        fels: 0,
        fahrrad: 0,
        elementar: 0,
        felsTouched: false,
        fahrradTouched: false,
        elementarTouched: false,
        vorhanden: action.payload.vorschaeden,
      };
    }
    case VhvActions.VORSCHAEDEN_FELS_CHANGED: {
      return { ...sectionState, fels: action.payload.fels, felsTouched: true };
    }
    case VhvActions.VORSCHAEDEN_FAHRRAD_CHANGED: {
      return { ...sectionState, fahrrad: action.payload.fahrrad, fahrradTouched: true };
    }
    case VhvActions.VORSCHAEDEN_ELEMENTAR_CHANGED: {
      return { ...sectionState, elementar: action.payload.elementar, elementarTouched: true };
    }
    case VhvActions.SHOW_ELEMENTAR_VORSCHADEN_ERSATZGESCHAEFT: {
      const isFirstActivation = action.payload && !sectionState.elementarVisibleInErsatzgeschaeft;
      return {
        ...sectionState,
        elementarVisibleInErsatzgeschaeft: action.payload,
        ...(isFirstActivation ? { vorhanden: null } : {}),
      };
    }
    case GlobalActions.ADD_VORVERTRAG_TO_ANGEBOTE: {
      const actionPayload: AddVertragToAngebote = action.payload;
      if (
        actionPayload.appId === App.VHV_V2 &&
        actionPayload.angebotsId === appState.angebotsId &&
        action.payload.vorvertragsNummern
      ) {
        const fels = resolveVorschadenArt(sectionState, 'fels', 0);
        const fahrrad = resolveVorschadenArt(sectionState, 'fahrrad', 0);
        const elementar = resolveVorschadenArt(sectionState, 'elementar', 0);
        return {
          ...sectionState,
          fels: fels.count,
          fahrrad: fahrrad.count,
          elementar: elementar.count,
          felsTouched: fels.touched,
          fahrradTouched: fahrrad.touched,
          elementarTouched: elementar.touched,
          vorhanden: fels.count > 0 || fahrrad.count > 0 || elementar.count > 0,
          elementarVisibleInErsatzgeschaeft: false,
        };
      }
      return sectionState;
    }
    case GlobalActions.VORBELEGUNG_FOR_BEIBEHALTUNG_REQUESTED: {
      const actionPayload: VhvVorbelegungdaten = action.payload;
      if (actionPayload.forApp === App.VHV_V2 && actionPayload.angebotsId === appState.angebotsId) {
        const anzVorschaedenFels = getAnzahlVorschaeden(
          actionPayload.vorvertragBausteinList,
          TaasTarifrisikoschluesselVHV.FELS,
        );
        const anzVorschaedenFahrrad = getAnzahlVorschaeden(
          actionPayload.vorvertragBausteinList,
          TaasTarifrisikoschluesselVHV.FAHRRAD,
        );
        const anzVorschaedenElementar = getAnzahlVorschaeden(
          actionPayload.vorvertragBausteinList,
          TaasTarifrisikoschluesselVHV.ELEMENTAR,
        );
        const fels = resolveVorschadenArt(sectionState, 'fels', anzVorschaedenFels);
        const fahrrad = resolveVorschadenArt(sectionState, 'fahrrad', anzVorschaedenFahrrad);
        const elementar = resolveVorschadenArt(sectionState, 'elementar', anzVorschaedenElementar);
        return {
          ...sectionState,
          vorhanden: fels.count > 0 || fahrrad.count > 0 || elementar.count > 0,
          fahrrad: fahrrad.count,
          fels: fels.count,
          elementar: elementar.count,
          felsTouched: fels.touched,
          fahrradTouched: fahrrad.touched,
          elementarTouched: elementar.touched,
        };
      }
      return sectionState;
    }
    case GlobalActions.VORBELEGUNG_FINISHED: {
      const actionPayload: VorbelegungPayload = action.payload;
      if (
        actionPayload.appId === App.VHV_V2 &&
        actionPayload.angebotsId === appState.angebotsId &&
        actionPayload.vorhandeneVorbelegungdaten &&
        !actionPayload.beibehalten
      ) {
        const vhvVorbelegungdaten: VhvVorbelegungdaten =
          actionPayload.vorhandeneVorbelegungdaten as VhvVorbelegungdaten;
        const anzVorschaedenFels = getAnzahlVorschaeden(
          vhvVorbelegungdaten.vorvertragBausteinList,
          TaasTarifrisikoschluesselVHV.FELS,
        );
        const anzVorschaedenFahrrad = getAnzahlVorschaeden(
          vhvVorbelegungdaten.vorvertragBausteinList,
          TaasTarifrisikoschluesselVHV.FAHRRAD,
        );
        const anzVorschaedenElementar = getAnzahlVorschaeden(
          vhvVorbelegungdaten.vorvertragBausteinList,
          TaasTarifrisikoschluesselVHV.ELEMENTAR,
        );
        const fels = resolveVorschadenArt(sectionState, 'fels', anzVorschaedenFels);
        const fahrrad = resolveVorschadenArt(sectionState, 'fahrrad', anzVorschaedenFahrrad);
        const elementar = resolveVorschadenArt(sectionState, 'elementar', anzVorschaedenElementar);
        return {
          ...sectionState,
          vorhanden: fels.count > 0 || fahrrad.count > 0 || elementar.count > 0,
          fels: fels.count,
          fahrrad: fahrrad.count,
          elementar: elementar.count,
          felsTouched: fels.touched,
          fahrradTouched: fahrrad.touched,
          elementarTouched: elementar.touched,
        };
      }
      return sectionState;
    }
    default:
      return sectionState;
  }
};
